import { Button, Flex, GridItem, Icon, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";
import { FaPlus } from "react-icons/fa";
import {
  ContactPersonDto,
  OrgPublicProfileDto,
  OrganizationEntryDto,
  OrganizationEntryPitchDto,
  OrganizationType,
  PersonDto,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { canUpdateOrgEntry } from "../../../../common/auth/utils";
import { displayPhoneNumber } from "../../../../common/formatting/displayPhoneNumber";
import { OrganizationTypeTag } from "../../../../common/organization/OrganizationTypeTag";
import { displayPersonName } from "../../../contracts/view-single/sharing/AddExternalParticipantModal";
import { OrgEntryContactPersonAddForm } from "./OrgEntryContactPersonAddForm";
import { OrgEntryContactPersonRow } from "./OrgEntryContactPersonRow";

interface ContactPersonsProps {
  organization?: OrgPublicProfileDto;
  organizationEntry?: OrganizationEntryDto;
}

export const ContactPersons = ({ organization, organizationEntry }: ContactPersonsProps) => {
  const authState = useLoggedInWithOrgContextState();
  const [isAdding, setIsAdding] = useState(false);

  if (!organization && !organizationEntry) return null;

  const mainOrgContactPersons:
    | {
        person: PersonDto;
        organization: OrgPublicProfileDto;
        organizationEntry?: never;
      }[]
    | {
        person: ContactPersonDto;
        organization?: never;
        organizationEntry: OrganizationEntryDto;
      }[] = organization
    ? (organization.contactPersons?.length ? organization.contactPersons : [organization.owner]).map((person) => ({
        person,
        organization,
      }))
    : organizationEntry?.contactPersons?.length
      ? organizationEntry.contactPersons.map((person) => ({
          person,
          organizationEntry,
        }))
      : [];

  const linkedEntryContactPersons = organization
    ? organization.linkedEntries
        .filter((organizationEntry) => organizationEntry.contactPersons && organizationEntry.contactPersons.length > 0)
        .flatMap((organizationEntry) => {
          const contactPersons = organizationEntry.contactPersons || [];
          return contactPersons.map((person) => ({
            person,
            organizationEntry,
          }));
        })
    : [];

  const contactPersonMap = new Map<
    string,
    | {
        person: PersonDto;
        organization: OrgPublicProfileDto;
        organizationEntry?: never;
      }
    | {
        person: ContactPersonDto;
        organization?: never;
        organizationEntry: OrganizationEntryDto | OrganizationEntryPitchDto;
      }
  >();

  mainOrgContactPersons.forEach((item) => {
    contactPersonMap.set(item.person.email.toLowerCase(), item);
  });

  linkedEntryContactPersons.forEach((item) => {
    if (!contactPersonMap.has(item.person.email.toLowerCase())) {
      contactPersonMap.set(item.person.email.toLowerCase(), item);
    }
  });

  const allContactPersons = Array.from(contactPersonMap.values());

  const candidateEntries: { id: string; name: string }[] = organization
    ? organization.linkedEntries.map((entry) => ({ id: entry.id, name: entry.name }))
    : organizationEntry
      ? [{ id: organizationEntry.id, name: organizationEntry.name }]
      : [];

  const canAddContactPerson = canUpdateOrgEntry(authState.selectedOrg.roles) && candidateEntries.length > 0;

  return (
    <GridItem colSpan={{ base: 1, lg: 2 }}>
      <Flex
        border="1px solid"
        borderColor="smBorder"
        flexDirection="column"
        alignItems="start"
        justifyContent="start"
        borderRadius="lg"
        bg="smBackground"
        width="full"
        maxWidth="full"
        height="auto"
        position="relative"
        p="6"
      >
        <Flex width="full" justifyContent="space-between" alignItems="center" pb="2" gap="2">
          <Text fontSize="md" fontWeight="bold">
            {t("Contact persons")}
          </Text>
          {canAddContactPerson && !isAdding && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              leftIcon={<Icon as={FaPlus} />}
              onClick={() => setIsAdding(true)}
            >
              {t("Add contact person")}
            </Button>
          )}
        </Flex>
        <Flex
          width="full"
          maxWidth="full"
          overflowX="auto"
          overflowY="auto"
          maxHeight="400px"
          border="1px solid"
          borderColor="smBorder"
          rounded="lg"
          pt="5"
          pb="2.5"
          position="relative"
        >
          <Table>
            <Thead>
              <Tr>
                <Th minWidth="60">{t("Name")}</Th>
                <Th>{t("Email")}</Th>
                <Th>{t("Phone Number")}</Th>
                <Th>{t("Title")}</Th>
                <Th textAlign="center">{t("Organization")}</Th>
                <Th textAlign="center">{t("Type")}</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {allContactPersons.map(({ person, organization, organizationEntry }) => {
                if (organization)
                  return (
                    <Tr key={person.email}>
                      <Td>{displayPersonName(person)}</Td>
                      <Td>{person.email}</Td>
                      <Td whiteSpace="nowrap">{person.phoneNumber ? displayPhoneNumber(person.phoneNumber) : null}</Td>
                      <Td>{person.corporateTitle}</Td>
                      <Td textAlign="center">{organization.name}</Td>
                      <Td textAlign="center">
                        <OrganizationTypeTag type={OrganizationType.Organization} size="sm" />
                      </Td>
                      <Td></Td>
                    </Tr>
                  );
                if (organizationEntry)
                  return (
                    <OrgEntryContactPersonRow
                      key={person.email}
                      person={person}
                      organizationEntry={organizationEntry}
                    />
                  );
              })}
            </Tbody>
          </Table>
        </Flex>
        {isAdding && <OrgEntryContactPersonAddForm entries={candidateEntries} onClose={() => setIsAdding(false)} />}
      </Flex>
    </GridItem>
  );
};
